/**
 * Weekly Global Opportunities review — top-ranked opportunities per suggested keyword.
 */

import { format, startOfWeek, differenceInCalendarDays, parseISO, isValid } from 'date-fns';
import type {
  OpportunityEntity,
  OpportunitySearchQueryResponse,
  RecommendedKeyword,
  RecommendedKeywordsResponse,
} from '../api/opportunities';
import type { GovtOpportunityEntity } from '../pages/government-data/OpportunitiesDashboard';
import {
  MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT,
  normalizeMonitorKeyword,
} from './alertKeywordRecommendations';
import {
  WEEKLY_REVIEW_MIN_AI_SOFTWARE,
  classifyKeywordDomain,
  type KeywordDomain,
} from './keywordDomain';
import {
  dataSourceMatchesGeographicZone,
  GEOGRAPHIC_ZONES,
  opportunityMatchesZone,
  WORLD_ZONE,
  type GeographicZoneId,
} from './countryAccess';
import { normalizeStringArray } from './normalizeStringArray';
import { normalizeOpportunityDeadline } from './opportunityUtils';

export { normalizeStringArray } from './normalizeStringArray';

/** Cache weekly review results for seven days (refreshed on manual refresh). */
export const WEEKLY_GLOBAL_OPPORTUNITIES_STALE_MS = 7 * 24 * 60 * 60 * 1000;

/** Top opportunities shown per suggested keyword (weekly review). */
export const TOP_OPPORTUNITIES_PER_KEYWORD = 5;

/**
 * Cap concurrent keyword fetch pipelines so Weekly Review does not stampede the Trace API
 * (each keyword runs field then semantic search). Pair with API_WORKERS >= this value in prod.
 */
export const WEEKLY_REVIEW_SEARCH_CONCURRENCY = 4;

let weeklyReviewSearchActive = 0;
const weeklyReviewSearchWaiters: Array<() => void> = [];

/** Serialize Weekly Review search work across keyword queryFns. */
export async function withWeeklyReviewSearchConcurrency<T>(
  fn: () => Promise<T>,
  limit: number = WEEKLY_REVIEW_SEARCH_CONCURRENCY
): Promise<T> {
  if (weeklyReviewSearchActive >= limit) {
    await new Promise<void>((resolve) => {
      weeklyReviewSearchWaiters.push(resolve);
    });
  }
  weeklyReviewSearchActive += 1;
  try {
    return await fn();
  } finally {
    weeklyReviewSearchActive -= 1;
    const next = weeklyReviewSearchWaiters.shift();
    if (next) next();
  }
}

const RAW_DATA_URL_KEYS = ['detail_url', 'uiLink', 'url', 'link', 'source_url'] as const;

function isValidHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

function extractUrlFromRawData(rawData: unknown): string | null {
  if (!rawData || typeof rawData !== 'object') return null;
  const record = rawData as Record<string, unknown>;
  for (const key of RAW_DATA_URL_KEYS) {
    const candidate = record[key];
    if (isValidHttpUrl(candidate)) return candidate.trim();
  }
  return null;
}

type UrlCheckEntity = {
  data?: {
    url?: unknown;
    detail_url?: unknown;
    raw_data?: unknown;
  } | null;
};

/** Resolve the first valid http(s) URL from entity data or raw_data. */
export function getOpportunityUrl(entity: UrlCheckEntity): string | null {
  const data = entity.data ?? {};
  if (isValidHttpUrl(data.url)) return data.url.trim();
  if (isValidHttpUrl(data.detail_url)) return data.detail_url.trim();
  return extractUrlFromRawData(data.raw_data);
}

/** Exclude opportunities with no clickable source link (garbage / mislabeled rows). */
export function hasValidOpportunityUrl(entity: UrlCheckEntity): boolean {
  return getOpportunityUrl(entity) != null;
}

export function filterEntitiesWithValidUrl<T extends UrlCheckEntity>(entities: T[]): T[] {
  return entities.filter(hasValidOpportunityUrl);
}

/** Drop rows with almost no lexical/keyword overlap unless portfolio score is already strong. */
export const WEEKLY_REVIEW_MIN_KEYWORD_FIT = 0.12;

/** Cross-keyword strips. */
export const WEEKLY_REVIEW_TOP_PICKS_LIMIT = 8;
export const WEEKLY_REVIEW_NEW_LIMIT = 6;
export const WEEKLY_REVIEW_EXPIRING_DAYS = 14;

/** BD proposal window — only surface open opps closing within this many days. */
export const WEEKLY_REVIEW_DEADLINE_HORIZON_DAYS = 21;

/** Grace for slightly overdue deadlines still counted as "open" in weekly shortlists. */
export const WEEKLY_REVIEW_OVERDUE_GRACE_DAYS = 0;

/** Look back this many days for recently missed (expired) opportunities. */
export const WEEKLY_REVIEW_RECENTLY_EXPIRED_LOOKBACK_DAYS = 14;

export const WEEKLY_REVIEW_RECENTLY_EXPIRED_LIMIT = 8;

const FIELD_CIVIL_NOISE =
  /\b(irrigation|wastewater|water well|civil works|construction|timber|harvest|brush clearing|road remediation|livestock|pasture|sardoba)\b/i;

export type WeeklyOpportunitySearchEntity = OpportunitySearchQueryResponse['entities'][number];

export interface KeywordOpportunitySection {
  keyword: string;
  confidencePct?: number;
  opportunities: OpportunityEntity[];
  isLoading: boolean;
  isFetching?: boolean;
  error?: string | null;
  /** 1–3 sentence AI (or fallback) trend blurb for this keyword section. */
  summary?: string | null;
  summaryLoading?: boolean;
}

export interface WeeklyDigestOpportunity extends OpportunityEntity {
  weeklyKeyword?: string;
  weeklyRelevance?: number;
  keywordFit?: number;
  /** Persisted SPARC portfolio match_score (0-1). Badge source of truth. */
  matchScore?: number;
}

export interface WeeklyDigestHighlights {
  topPicks: WeeklyDigestOpportunity[];
  newThisWeek: WeeklyDigestOpportunity[];
  expiringSoon: WeeklyDigestOpportunity[];
  recentlyExpiredMissed: WeeklyDigestOpportunity[];
}

type DeadlineCheckEntity = {
  data?: Record<string, unknown> | null;
};

/** Coerce recommended-keywords entries when the API returns a non-array payload. */
export function normalizeRecommendedKeywordEntries(value: unknown): RecommendedKeyword[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is RecommendedKeyword =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as RecommendedKeyword).keyword === 'string' &&
        (item as RecommendedKeyword).keyword.trim().length > 0
    );
  }
  if (value && typeof value === 'object' && typeof (value as RecommendedKeyword).keyword === 'string') {
    return [value as RecommendedKeyword];
  }
  return [];
}

export function normalizeRecommendedKeywordsResponse(
  data: RecommendedKeywordsResponse | null | undefined
): RecommendedKeywordsResponse | undefined {
  if (!data) return undefined;

  return {
    ...data,
    keywords: normalizeRecommendedKeywordEntries(data.keywords),
    service_keyword_groups: Array.isArray(data.service_keyword_groups)
      ? data.service_keyword_groups
      : [],
  };
}

/**
 * Weekly review keyword selection — prioritize RAG-suggested keywords with
 * domain diversity (reserve AI/software slots before GIS/environmental fill).
 * Returns all keywords above MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT (no cap).
 */
export function extractWeeklyReviewKeywords(
  data: Pick<RecommendedKeywordsResponse, 'keywords' | 'service_keyword_groups'> | null | undefined
): string[] {
  if (!data) return [];

  const sortedKeywords = normalizeRecommendedKeywordEntries(data.keywords)
    .filter((kw) => (kw.confidence_pct ?? 0) > MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT)
    .sort((a, b) => (b.confidence_pct ?? 0) - (a.confidence_pct ?? 0));

  const results: string[] = [];
  const seen = new Set<string>();
  const domainCounts: Record<KeywordDomain, number> = {
    ai_software: 0,
    gis_spatial: 0,
    environmental: 0,
    engineering: 0,
    general: 0,
  };

  const add = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return false;
    const key = normalizeMonitorKeyword(trimmed);
    if (seen.has(key)) return false;
    seen.add(key);
    results.push(trimmed);
    domainCounts[classifyKeywordDomain(trimmed)] += 1;
    return true;
  };

  const aiCandidates = sortedKeywords.filter(
    (kw) => classifyKeywordDomain(kw.keyword) === 'ai_software'
  );
  for (const kw of aiCandidates) {
    if (domainCounts.ai_software >= WEEKLY_REVIEW_MIN_AI_SOFTWARE) break;
    add(kw.keyword);
  }

  for (const kw of sortedKeywords) {
    add(kw.keyword);
  }

  for (const group of data.service_keyword_groups ?? []) {
    for (const sub of group.subcategories ?? []) {
      if (sub.title) add(sub.title);
    }
  }

  return results;
}

/** India/state portal markers — sam.gov-labeled rows with these are often index pollution. */
const INDIA_PORTAL_MARKERS =
  /\b(india|odisha|ofsdp|eprocure|tender247|tendertiger|tender odisha)\b/i;

const WEEKLY_OPPORTUNITY_ENTITY_TYPES = new Set(['govt_opportunity', 'rfp_opportunity']);

/** @deprecated Prefer getOpportunityUrl — kept for existing imports. */
export function getWeeklyOpportunityExternalUrl(
  entity: WeeklyOpportunitySearchEntity
): string | null {
  return getOpportunityUrl(entity);
}

/**
 * Weekly Review quality gate: real portal tenders with external links only.
 * Filters past_project / wrong entity_type hits and sam.gov rows that are
 * clearly India/Odisha portal paste (semantic search index pollution).
 */
export function isUsableWeeklyOpportunity(entity: WeeklyOpportunitySearchEntity): boolean {
  const data = entity.data ?? {};
  const entityType = String(entity.entity_type ?? data.entity_type ?? '').trim();

  if (entityType && !WEEKLY_OPPORTUNITY_ENTITY_TYPES.has(entityType)) {
    return false;
  }

  if (!hasValidOpportunityUrl(entity)) {
    return false;
  }

  const source = String(data.source ?? '').trim().toLowerCase();
  const title = String(data.title ?? '');
  const org = String(data.organization ?? data.issuing_agency ?? data.agency ?? '');
  const textBlob = `${title} ${org}`.toLowerCase();

  if (source === 'samgov' && INDIA_PORTAL_MARKERS.test(textBlob)) {
    return false;
  }

  return true;
}

export function filterUsableWeeklyOpportunities(
  entities: WeeklyOpportunitySearchEntity[]
): WeeklyOpportunitySearchEntity[] {
  return entities.filter(isUsableWeeklyOpportunity);
}

export function getGeographicZoneDisplayLabel(zoneId: GeographicZoneId): string {
  if (zoneId === 'world') return WORLD_ZONE.label;
  return GEOGRAPHIC_ZONES.find((zone) => zone.id === zoneId)?.label ?? zoneId;
}

export function filterWeeklyEntitiesByGeographicZone(
  entities: WeeklyOpportunitySearchEntity[],
  zoneId: GeographicZoneId
): WeeklyOpportunitySearchEntity[] {
  if (zoneId === 'world') return entities;

  return entities.filter((entity) => {
    const data = entity.data ?? {};
    const country = data.country ?? data.geographic_scope ?? data.region;
    if (opportunityMatchesZone(typeof country === 'string' ? country : undefined, zoneId)) {
      return true;
    }

    const source = String(data.source ?? '').trim();
    return source.length > 0 && dataSourceMatchesGeographicZone(source, zoneId);
  });
}

function tokenizeKeyword(keyword: string): string[] {
  return keyword
    .toLowerCase()
    .split(/[^a-z0-9]+/g)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !['and', 'or', 'the', 'for', 'of', 'in', 'to', 'a'].includes(t));
}

function entitySearchText(entity: WeeklyOpportunitySearchEntity): string {
  const data = entity.data ?? {};
  const parts = [
    data.title,
    data.description,
    data.ai_card_summary,
    data.organization,
    ...(normalizeStringArray(data.keywords) || []),
    ...(normalizeStringArray(data.service_lines) || []),
  ];
  return parts
    .filter((p) => typeof p === 'string' && p.trim())
    .join(' ')
    .toLowerCase();
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/**
 * Lexical keyword fit (0–1): how strongly the opportunity text contains the suggested keyword tokens.
 * Strong title hits score high so Weekly Review can show 50%+ when the keyword truly matches.
 */
export function scoreKeywordLexicalFit(
  keyword: string,
  entity: WeeklyOpportunitySearchEntity
): number {
  const tokens = tokenizeKeyword(keyword);
  if (tokens.length === 0) return 0;

  const data = entity.data ?? {};
  const title = String(data.title ?? '').toLowerCase();
  const haystack = entitySearchText(entity);
  const phrase = keyword.trim().toLowerCase();

  let score = 0;
  if (phrase.length >= 4 && title.includes(phrase)) score += 0.72;
  else if (phrase.length >= 4 && haystack.includes(phrase)) score += 0.48;

  let hits = 0;
  let titleHits = 0;
  for (const token of tokens) {
    if (title.includes(token)) {
      hits += 1;
      titleHits += 1;
    } else if (haystack.includes(token)) {
      hits += 1;
    }
  }
  const coverage = hits / tokens.length;
  score += 0.45 * coverage;
  if (titleHits > 0) score += 0.18 * (titleHits / tokens.length);

  // Distinctive title tokens (e.g. "GIS", "DSS", "AI") are high-signal even when
  // companion words like "Application" are missing from the tender title.
  if (titleHits > 0 && coverage >= 0.4) {
    score = Math.max(score, 0.58 + 0.25 * coverage);
  }
  if (titleHits === tokens.length && tokens.length > 0) {
    score = Math.max(score, 0.78);
  }

  return clamp01(score);
}

function portfolioMatchScore(entity: WeeklyOpportunitySearchEntity): number {
  return getPersistedMatchScore(entity) ?? 0;
}

/**
 * Persisted SPARC portfolio match (0-1). Same value as Opportunities tab Match %.
 * Accepts 0-1 or 0-100 storage. Undefined when missing.
 */
export function getPersistedMatchScore(
  entity: { data?: { match_score?: unknown } | null }
): number | undefined {
  const raw = entity.data?.match_score;
  if (typeof raw !== 'number' || Number.isNaN(raw) || raw <= 0) return undefined;
  return raw > 1 ? clamp01(raw / 100) : clamp01(raw);
}

/** Display name of the user who set tracking_status (entity data.assigned_by). */
export function getTrackingOwnerName(
  data: { assigned_by?: unknown; tracked_by?: unknown } | null | undefined
): string | null {
  for (const raw of [data?.assigned_by, data?.tracked_by]) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

export function patchWeeklyOpportunityTrackingData<T extends { id?: unknown; data?: Record<string, unknown> | null }>(
  entities: T[] | undefined,
  entityId: string,
  patch: { tracking_status?: string | null; assigned_by?: string | null }
): T[] | undefined {
  if (!Array.isArray(entities)) return entities;
  return entities.map((entity) => {
    if (String(entity.id) !== entityId) return entity;
    return {
      ...entity,
      data: {
        ...(entity.data || {}),
        tracking_status: patch.tracking_status,
        assigned_by: patch.assigned_by,
      },
    };
  });
}

export function trackingOwnerIndexFromEntities(
  entities: Array<{ id?: unknown; data?: Record<string, unknown> | null }>
): Map<string, { assigned_by?: unknown; tracking_status?: unknown }> {
  const map = new Map<string, { assigned_by?: unknown; tracking_status?: unknown }>();
  for (const entity of entities) {
    const data = entity.data || {};
    if (data.assigned_by == null && data.tracking_status == null) continue;
    map.set(String(entity.id), {
      assigned_by: data.assigned_by,
      tracking_status: data.tracking_status,
    });
  }
  return map;
}

/** Overlay live tracking fields onto weekly search hits (search cache can be stale). */
export function mergeTrackingOwnerOntoEntities<
  T extends { id?: unknown; data?: Record<string, unknown> | null },
>(
  entities: T[],
  trackingById: ReadonlyMap<string, { assigned_by?: unknown; tracking_status?: unknown }>
): T[] {
  if (trackingById.size === 0) return entities;
  return entities.map((entity) => {
    const patch = trackingById.get(String(entity.id));
    if (!patch) return entity;
    return {
      ...entity,
      data: {
        ...(entity.data || {}),
        tracking_status: patch.tracking_status ?? entity.data?.tracking_status,
        assigned_by: patch.assigned_by ?? entity.data?.assigned_by,
      },
    };
  });
}

function searchSimilarity(entity: WeeklyOpportunitySearchEntity): number {
  const raw = entity.search_score;
  if (typeof raw !== 'number' || Number.isNaN(raw)) return 0;
  return clamp01(raw);
}

/**
 * Composite weekly relevance for a keyword section (ranking within that keyword only).
 * The Match badge uses persisted match_score via getPersistedMatchScore, not this value.
 */
export function scoreWeeklyKeywordRelevance(
  keyword: string,
  entity: WeeklyOpportunitySearchEntity
): number {
  const keywordFit = scoreKeywordLexicalFit(keyword, entity);
  const portfolio = portfolioMatchScore(entity);
  const semantic = searchSimilarity(entity);
  const domain = classifyKeywordDomain(keyword);

  let score = 0.55 * keywordFit + 0.3 * portfolio + 0.15 * semantic;

  // Soft floor when lexical fit is clearly present — BD expects 50%+ for real keyword hits.
  if (keywordFit >= 0.55) {
    score = Math.max(score, 0.55 + 0.35 * keywordFit);
  } else if (keywordFit >= 0.4) {
    score = Math.max(score, 0.5 + 0.25 * keywordFit);
  }

  const title = String(entity.data?.title ?? '');
  if (domain === 'ai_software' && FIELD_CIVIL_NOISE.test(title) && keywordFit < 0.45) {
    score *= 0.45;
  }

  return clamp01(score);
}

export function mergeWeeklySearchEntities(
  ...lists: WeeklyOpportunitySearchEntity[][]
): WeeklyOpportunitySearchEntity[] {
  const byId = new Map<string, WeeklyOpportunitySearchEntity>();
  for (const list of lists) {
    for (const entity of list) {
      const id = String(entity.id);
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, entity);
        continue;
      }
      const bestScore = Math.max(existing.search_score ?? 0, entity.search_score ?? 0);
      byId.set(id, {
        ...existing,
        ...entity,
        data: { ...(existing.data || {}), ...(entity.data || {}) },
        search_score: bestScore,
        search_tier: entity.search_tier || existing.search_tier,
      });
    }
  }
  return Array.from(byId.values());
}

export function pickTopSearchOpportunities(
  entities: WeeklyOpportunitySearchEntity[],
  limit: number = TOP_OPPORTUNITIES_PER_KEYWORD,
  keyword?: string,
  options?: { now?: Date }
): WeeklyOpportunitySearchEntity[] {
  const now = options?.now ?? new Date();
  const usableEntities = filterWeeklyReviewHorizonEntities(
    filterUsableWeeklyOpportunities(entities),
    now
  );
  const ranked = [...usableEntities].map((entity) => {
    const keywordFit = keyword ? scoreKeywordLexicalFit(keyword, entity) : 0;
    const weeklyRelevance = keyword
      ? scoreWeeklyKeywordRelevance(keyword, entity)
      : searchSimilarity(entity);
    return { entity, keywordFit, weeklyRelevance };
  });

  ranked.sort((a, b) => {
    if (b.weeklyRelevance !== a.weeklyRelevance) return b.weeklyRelevance - a.weeklyRelevance;
    if (b.keywordFit !== a.keywordFit) return b.keywordFit - a.keywordFit;
    return (b.entity.search_score ?? 0) - (a.entity.search_score ?? 0);
  });

  const filtered = keyword
    ? ranked.filter(
        (row) =>
          row.keywordFit >= WEEKLY_REVIEW_MIN_KEYWORD_FIT ||
          portfolioMatchScore(row.entity) >= 0.45 ||
          row.weeklyRelevance >= 0.35
      )
    : ranked;

  const pool = filtered.length > 0 ? filtered : ranked;
  return pool.slice(0, limit).map((row) => ({
    ...row.entity,
    data: {
      ...(row.entity.data || {}),
      weekly_keyword_fit: row.keywordFit,
      weekly_relevance_score: row.weeklyRelevance,
    },
  }));
}

export function mapSearchEntityToOpportunityEntity(
  entity: WeeklyOpportunitySearchEntity
): OpportunityEntity {
  const data = entity.data ?? {};
  const now = new Date().toISOString();
  const normalizedKeywords = normalizeStringArray(data.keywords);
  const normalizedServiceLines = normalizeStringArray(data.service_lines);
  const rawDeadline = data.deadline ?? data.closing_date ?? data.response_deadline;
  const normalizedDeadline = normalizeOpportunityDeadline(rawDeadline);
  const normalizedClosingDate = normalizeOpportunityDeadline(data.closing_date) ?? normalizedDeadline;
  const externalUrl = getOpportunityUrl(entity) ?? '';

  return {
    id: entity.id,
    entity_type: 'rfp_opportunity',
    data: {
      ...data,
      opportunity_id: String(data.opportunity_id ?? data.external_id ?? entity.id),
      source: data.source ?? 'samgov',
      title: data.title ?? 'Untitled opportunity',
      description: data.description ?? '',
      organization: data.organization ?? data.issuing_agency ?? data.agency ?? '',
      deadline: normalizedDeadline ?? '',
      closing_date: normalizedClosingDate ?? '',
      value: data.value ?? data.estimated_value,
      url: externalUrl,
      keywords: normalizedKeywords,
      service_lines: normalizedServiceLines,
      status: data.status,
      search_score: entity.search_score,
      search_tier: entity.search_tier,
      weekly_keyword_fit: data.weekly_keyword_fit,
      weekly_relevance_score: data.weekly_relevance_score,
      created_at: entity.created_at ?? now,
      updated_at: entity.updated_at ?? now,
    },
    created_at: entity.created_at ?? now,
    updated_at: entity.updated_at ?? now,
  } as OpportunityEntity;
}

export function mapSearchEntityToGovtOpportunity(
  entity: WeeklyOpportunitySearchEntity
): GovtOpportunityEntity {
  const mapped = mapSearchEntityToOpportunityEntity(entity);

  return {
    ...mapped,
    entity_type: entity.entity_type || 'govt_opportunity',
    data: {
      ...mapped.data,
      search_score: entity.search_score,
      search_tier: entity.search_tier,
    },
  } as GovtOpportunityEntity;
}

export function getWeeklyReviewWeekLabel(date: Date = new Date()): string {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  return format(weekStart, 'MMM d, yyyy');
}

export function getWeeklyReviewWeekStart(date: Date = new Date()): Date {
  return startOfWeek(date, { weekStartsOn: 1 });
}

function parseEntityDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function extractDeadlineRaw(data: Record<string, unknown> | null | undefined): string | undefined {
  if (!data) return undefined;
  const raw = data.deadline ?? data.closing_date ?? data.response_deadline;
  return typeof raw === 'string' && raw.trim() ? raw : undefined;
}

/** Calendar days until deadline (negative = expired). Returns null when no parseable deadline. */
export function getOpportunityDaysUntilDeadline(
  opp: DeadlineCheckEntity,
  now: Date = new Date()
): number | null {
  const raw = extractDeadlineRaw(opp.data ?? undefined);
  if (!raw) return null;
  const normalized = normalizeOpportunityDeadline(raw);
  const d = parseEntityDate(normalized ?? raw);
  if (!d) return null;
  return differenceInCalendarDays(d, now);
}

export function isWithinWeeklyReviewDeadlineHorizon(
  opp: DeadlineCheckEntity,
  now: Date = new Date(),
  options?: { graceDays?: number }
): boolean {
  const days = getOpportunityDaysUntilDeadline(opp, now);
  if (days === null) return false;
  const grace = options?.graceDays ?? WEEKLY_REVIEW_OVERDUE_GRACE_DAYS;
  return days >= -grace && days <= WEEKLY_REVIEW_DEADLINE_HORIZON_DAYS;
}

export function isRecentlyExpiredMissedOpportunity(
  opp: DeadlineCheckEntity,
  now: Date = new Date(),
  options?: { lookbackDays?: number }
): boolean {
  const days = getOpportunityDaysUntilDeadline(opp, now);
  if (days === null) return false;
  const lookback = options?.lookbackDays ?? WEEKLY_REVIEW_RECENTLY_EXPIRED_LOOKBACK_DAYS;
  return days < 0 && days >= -lookback;
}

export function filterWeeklyReviewHorizonEntities<T extends DeadlineCheckEntity>(
  entities: T[],
  now: Date = new Date()
): T[] {
  return entities.filter((entity) => isWithinWeeklyReviewDeadlineHorizon(entity, now));
}

function daysUntilDeadline(opp: OpportunityEntity, now: Date): number | null {
  return getOpportunityDaysUntilDeadline(opp, now);
}

function hasDecentWeeklyRelevance(
  entity: WeeklyOpportunitySearchEntity,
  keywordFit: number,
  weeklyRelevance: number
): boolean {
  return (
    keywordFit >= WEEKLY_REVIEW_MIN_KEYWORD_FIT ||
    portfolioMatchScore(entity) >= 0.45 ||
    weeklyRelevance >= 0.35
  );
}

/** Recently expired opps from keyword search pools — separate from the 21-day ahead shortlists. */
export function buildRecentlyExpiredMissed(
  keywordPools: Array<{ keyword: string; entities: WeeklyOpportunitySearchEntity[] }>,
  options?: { now?: Date; limit?: number }
): WeeklyDigestOpportunity[] {
  const now = options?.now ?? new Date();
  const limit = options?.limit ?? WEEKLY_REVIEW_RECENTLY_EXPIRED_LIMIT;
  const flat: WeeklyDigestOpportunity[] = [];

  for (const { keyword, entities } of keywordPools) {
    for (const entity of filterUsableWeeklyOpportunities(entities)) {
      if (!isRecentlyExpiredMissedOpportunity(entity, now)) continue;
      const keywordFit = scoreKeywordLexicalFit(keyword, entity);
      const weeklyRelevance = scoreWeeklyKeywordRelevance(keyword, entity);
      if (!hasDecentWeeklyRelevance(entity, keywordFit, weeklyRelevance)) continue;

      flat.push({
        ...mapSearchEntityToOpportunityEntity(entity),
        weeklyKeyword: keyword,
        weeklyRelevance,
        keywordFit,
        matchScore: getPersistedMatchScore(entity),
      });
    }
  }

  const byId = new Map<string, WeeklyDigestOpportunity>();
  for (const opp of flat) {
    const existing = byId.get(opp.id);
    if (!existing || (opp.weeklyRelevance ?? 0) > (existing.weeklyRelevance ?? 0)) {
      byId.set(opp.id, opp);
    }
  }

  return Array.from(byId.values())
    .sort((a, b) => {
      const daysA = getOpportunityDaysUntilDeadline(a, now) ?? -999;
      const daysB = getOpportunityDaysUntilDeadline(b, now) ?? -999;
      if (daysB !== daysA) return daysB - daysA;
      return (b.weeklyRelevance ?? 0) - (a.weeklyRelevance ?? 0);
    })
    .slice(0, limit);
}

function isNewThisWeek(opp: OpportunityEntity, weekStart: Date): boolean {
  const published =
    parseEntityDate(opp.data?.published_date) ||
    parseEntityDate(opp.data?.publish_date) ||
    parseEntityDate(opp.created_at);
  if (!published) return false;
  return published >= weekStart;
}

/** Flatten sections into digest strips: top picks, new, expiring soon. */
export function buildWeeklyDigestHighlights(
  sections: KeywordOpportunitySection[],
  options?: { now?: Date; weekStart?: Date }
): WeeklyDigestHighlights {
  const now = options?.now ?? new Date();
  const weekStart = options?.weekStart ?? getWeeklyReviewWeekStart(now);

  const flat: WeeklyDigestOpportunity[] = [];
  for (const section of sections) {
    for (const opp of section.opportunities) {
      if (!hasValidOpportunityUrl(opp)) continue;
      const weeklyRelevance =
        typeof opp.data?.weekly_relevance_score === 'number'
          ? opp.data.weekly_relevance_score
          : typeof opp.data?.match_score === 'number'
            ? opp.data.match_score
            : 0;
      const keywordFit =
        typeof opp.data?.weekly_keyword_fit === 'number' ? opp.data.weekly_keyword_fit : 0;
      flat.push({
        ...opp,
        weeklyKeyword: section.keyword,
        weeklyRelevance,
        keywordFit,
        matchScore: getPersistedMatchScore(opp),
      });
    }
  }

  const byId = new Map<string, WeeklyDigestOpportunity>();
  for (const opp of flat) {
    const existing = byId.get(opp.id);
    if (!existing || (opp.weeklyRelevance ?? 0) > (existing.weeklyRelevance ?? 0)) {
      byId.set(opp.id, opp);
    }
  }
  const unique = Array.from(byId.values()).filter((opp) =>
    isWithinWeeklyReviewDeadlineHorizon(opp, now)
  );

  const expiringHorizonDays = Math.min(
    WEEKLY_REVIEW_EXPIRING_DAYS,
    WEEKLY_REVIEW_DEADLINE_HORIZON_DAYS
  );

  const topPicks = [...unique]
    .sort((a, b) => {
      const matchA = a.matchScore ?? 0;
      const matchB = b.matchScore ?? 0;
      if (matchB !== matchA) return matchB - matchA;
      return (b.weeklyRelevance ?? 0) - (a.weeklyRelevance ?? 0);
    })
    .slice(0, WEEKLY_REVIEW_TOP_PICKS_LIMIT);

  const newThisWeek = unique
    .filter((opp) => isNewThisWeek(opp, weekStart))
    .sort((a, b) => {
      const matchA = a.matchScore ?? 0;
      const matchB = b.matchScore ?? 0;
      if (matchB !== matchA) return matchB - matchA;
      return (b.weeklyRelevance ?? 0) - (a.weeklyRelevance ?? 0);
    })
    .slice(0, WEEKLY_REVIEW_NEW_LIMIT);

  const expiringSoon = unique
    .map((opp) => ({ opp, days: daysUntilDeadline(opp, now) }))
    .filter(
      (row) =>
        row.days != null && row.days >= 0 && row.days <= expiringHorizonDays
    )
    .sort((a, b) => (a.days ?? 99) - (b.days ?? 99))
    .slice(0, WEEKLY_REVIEW_NEW_LIMIT)
    .map((row) => row.opp);

  return { topPicks, newThisWeek, expiringSoon, recentlyExpiredMissed: [] };
}

/** Deterministic fallback when LLM summary is unavailable. */
export function buildFallbackKeywordSummary(
  keyword: string,
  opportunities: OpportunityEntity[]
): string {
  if (opportunities.length === 0) {
    return `No open opportunities matched “${keyword}” in this week’s scan. Broaden region filters or refresh after the next portal sync.`;
  }
  const orgs = opportunities
    .map((o) => o.data?.organization)
    .filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
    .slice(0, 3);
  const urgent = opportunities.filter((o) => {
    const d = daysUntilDeadline(o, new Date());
    return d != null && d >= 0 && d <= WEEKLY_REVIEW_EXPIRING_DAYS;
  }).length;
  const orgBit = orgs.length ? ` Notable buyers include ${orgs.join('; ')}.` : '';
  const urgentBit =
    urgent > 0
      ? ` ${urgent} of ${opportunities.length} close within two weeks.`
      : ` ${opportunities.length} opportunities are in the current shortlist.`;
  return `This week’s “${keyword}” shortlist leans on keyword-aligned tenders.${orgBit}${urgentBit}`;
}

export function buildFallbackPageSummary(
  sections: KeywordOpportunitySection[],
  highlights: WeeklyDigestHighlights
): string {
  const active = sections.filter((s) => s.opportunities.length > 0);
  const kwList = active
    .slice(0, 4)
    .map((s) => s.keyword)
    .join(', ');
  const top = highlights.topPicks[0]?.data?.title;
  const topBit = top ? ` Strongest cross-keyword pick: “${top}”.` : '';
  return `Weekly scan covers ${active.length} keyword themes${kwList ? ` (${kwList}${active.length > 4 ? ', …' : ''})` : ''}, with ${highlights.newThisWeek.length} new and ${highlights.expiringSoon.length} expiring soon.${topBit}`;
}
